import { useParams, useSearch } from '@tanstack/react-router';
import { getEventAttendee } from 'src/graphql/queries/eventAttendee.graphql';
import type {
  IGetEventAttendeeQuery,
  IGetEventAttendeeQueryVariables,
} from 'src/graphql/queries/eventAttendee.graphql.types';
import { useQuery } from 'urql';
import usePermission from '../permissions/usePermissions';
import usePlayerWithCharactersInOrganization from '../players/usePlayerWithCharactersInOrganization';
import useRootUser from '../players/useRootUser';
import useRootUserRevalidation from '../useRootUserRevalidation';

const useEventAttendee = () => {
  const {
    rootUser: { id },
  } = useRootUser();
  const { eventId } = useParams({ strict: false });
  const { player_id: playerIdFromURL } = useSearch({ strict: false });
  const { isPermitted: canAssistCheckin } = usePermission({
    action: 'checkin_character',
    eventId: Number(eventId),
  });

  const playerId = canAssistCheckin ? Number(playerIdFromURL ?? id) : id;
  const { player, characters } = usePlayerWithCharactersInOrganization({
    playerId,
  });

  const [{ data, error, fetching, stale }, refetch] = useQuery<
    IGetEventAttendeeQuery,
    IGetEventAttendeeQueryVariables
  >({
    query: getEventAttendee,
    variables: {
      eventId: Number(eventId),
      playerId,
    },
    requestPolicy: 'network-only',
    pause: Number(eventId) === 0 || !playerId,
  });

  useRootUserRevalidation({ refetch });

  return {
    error,
    fetching,
    stale,
    refetch,
    event: data?.event,
    eventAttendee: data?.event?.eventAttendee,
    player,
    characters,
  };
};

export default useEventAttendee;
